import { css } from "@emotion/react";
import type { NextPage } from "next";
import Link from "next/link";
import { useMemo } from "react";
import { match } from "ts-pattern";

import CreateAccountLead from "~/components/common/CreateAccountLead";
import LoadingIndicator from "~/components/common/LoadingIndicator";
import PageHead from "~/components/common/PageHead";
import RetriableError from "~/components/me/bento/RetriableError";
import {
  HistoryCard,
  buildExamHistoryItems,
} from "~/components/me/bento/content/exam-history";
import { useMeExamHistory } from "~/components/me/bento/lib";
import { useGraphqlClient } from "~/lib/client/hooks/useGraphqlClient";
import { Layout } from "~/lib/client/layout";
import { MeExamHistoryQuery, Sdk } from "~/lib/graphql/client";
import { useAppContext } from "~/pages/_app";

const ExamHistoryPage: NextPage = () => {
  const client = useGraphqlClient();
  const { currentUser } = useAppContext();

  if (currentUser === undefined || client === undefined) {
    return (
      <>
        <PageHead title="受験履歴" />
        <Layout layoutType={"YTLayout"}>
          <main css={pageWrapperStyle}>
            <LoadingIndicator />
          </main>
        </Layout>
      </>
    );
  }

  if (!currentUser) {
    return (
      <>
        <PageHead title="受験履歴" />
        <Layout layoutType={"YTLayout"}>
          <main css={pageWrapperStyle}>
            <CreateAccountLead label={"ログインが必要な画面です。"} />
          </main>
        </Layout>
      </>
    );
  }

  if (!client) {
    return (
      <>
        <PageHead title="受験履歴" />
        <Layout layoutType={"YTLayout"}>
          <main css={pageWrapperStyle}>
            <RetriableError reload={() => window.location.reload()} />
          </main>
        </Layout>
      </>
    );
  }

  return (
    <>
      <PageHead title="受験履歴" />
      <Layout layoutType={"YTLayout"}>
        <main css={pageWrapperStyle}>
          <header css={headerStyle}>
            <Link href="/me" css={backLinkStyle}>
              マイページに戻る
            </Link>
            <h1 css={titleStyle}>受験履歴</h1>
            <p css={descriptionStyle}>
              これまでに受験した検定の履歴を最新順に表示しています。
            </p>
          </header>
          <ExamHistoryContent client={client} />
        </main>
      </Layout>
    </>
  );
};

const ExamHistoryContent = ({ client }: { client: Sdk }) => {
  const result = useMeExamHistory(client);

  return match(result)
    .with({ status: "pending" }, () => (
      <div css={loadingAreaStyle}>
        <LoadingIndicator />
      </div>
    ))
    .with({ status: "error" }, (matched) => (
      <RetriableError reload={matched.refetch} />
    ))
    .with({ status: "success" }, (matched) => (
      <FullHistoryBody content={matched.data} />
    ))
    .exhaustive();
};

const FullHistoryBody = ({ content }: { content: MeExamHistoryQuery }) => {
  const sorted = useMemo(() => {
    return buildExamHistoryItems(content);
  }, [content]);

  if (sorted.length === 0) {
    return <p css={emptyMessageStyle}>まだ受験履歴がありません</p>;
  }

  return (
    <div css={fullListContainerStyle} role={"list"}>
      {sorted.map((item) => (
        <HistoryCard
          key={
            item.kind === "result"
              ? `result-${item.result.id}`
              : `purchase-${item.purchase.id}`
          }
          item={item}
        />
      ))}
    </div>
  );
};

const pageWrapperStyle = css`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const backLinkStyle = css`
  font-size: 0.75rem;
  color: rgba(15, 23, 42, 0.6);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const titleStyle = css`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
`;

const descriptionStyle = css`
  font-size: 0.85rem;
  color: rgba(15, 23, 42, 0.75);
  margin: 0;
`;

const fullListContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const emptyMessageStyle = css`
  font-size: 0.9rem;
  color: rgba(15, 23, 42, 0.75);
`;

const loadingAreaStyle = css`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
`;

export default ExamHistoryPage;
